// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Prompt Version Manager
// Route: /prompts/$promptKey
// apps/web/src/routes/prompts.$promptKey.tsx
//
// Detail view for a single prompt definition. Owner/Admins can:
//   • View the full history of versions (including decrypted system / user
//     text for any version)
//   • Create a new draft version from the currently-active one (paste new
//     system prompt text, adjust model/temp/tokens, add author notes)
//   • Dry-run a version against one ImportedConversation without persisting
//   • Activate a draft (automatically deactivates the prior active)
//
// This is the UI that replaces the PowerShell workflow in the deploy docs.
// Every action maps 1:1 to a PromptManagementController endpoint.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  StatusBadge,
  LoadingShimmer,
  EmptyState,
} from '../components/engine/EnginePageShell';
import { useApiClient } from '@forgetrack/api-client';

export const Route = createFileRoute('/prompts/$promptKey')({
  component: PromptDetailPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────

interface PromptDefinition {
  id: string;
  promptKey: string;
  displayName: string;
  description: string;
  category: string;
  activeVersion: number | null;
  activeVersionId?: string | null;
  activeModelName: string | null;
}

interface PromptVersionSummary {
  id: string;
  version: number;
  status: string; // "active" | "draft" | "archived"
  modelName: string;
  temperature: number;
  maxTokens: number;
  authorNotes: string | null;
  createdAt: string;
  promotedAt: string | null;
  avgCostUSD: number;
  invocationCount: number;
}

interface PromptVersionFull extends PromptVersionSummary {
  systemPromptText: string;
  userTemplateText: string;
}

interface TestResult {
  success:    boolean;
  outputJson: string | null;
  tokensIn:   number;
  tokensOut:  number;
  costUSD:    number;
  latencyMs:  number;
  error:      string | null;
}

// ─── Page ───────────────────────────────────────────────────────────────────

function PromptDetailPage() {
  const { promptKey } = Route.useParams();
  const { createApi } = useApiClient();

  const [def, setDef]             = useState<PromptDefinition | null>(null);
  const [versions, setVersions]   = useState<PromptVersionSummary[] | null>(null);
  const [loading, setLoading]     = useState(true);
  const [errorMsg, setErrorMsg]   = useState<string | null>(null);

  // Drawer / modal state
  const [newVersionOpen, setNewVersionOpen]   = useState(false);
  const [viewVersionId, setViewVersionId]     = useState<string | null>(null);
  const [testVersionId, setTestVersionId]     = useState<string | null>(null);
  const [activateCandidate, setActivateCandidate] = useState<PromptVersionSummary | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const api = await createApi();
      const [defRes, versionsRes] = await Promise.all([
        api.get(`/api/v1/prompts/definitions/${encodeURIComponent(promptKey)}`),
        api.get(`/api/v1/prompts/definitions/${encodeURIComponent(promptKey)}/versions`),
      ]);
      setDef(defRes.data);
      setVersions(versionsRes.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load prompt';
      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  }, [createApi, promptKey]);

  useEffect(() => { refresh(); }, [refresh]);

  // Case-insensitive — the backend is lowercase today but legacy rows
  // and future refactors could drift; the button must not silently disable
  // when the shape is close-but-not-exact.
  const activeVersion =
    versions?.find(v => (v.status ?? '').toLowerCase() === 'active')
    ?? versions?.find(v => (v.status ?? '').toLowerCase() === 'promoted')
    ?? null;

  return (
    <EnginePageShell
      title={def?.displayName ?? promptKey}
      subtitle={def?.description ?? 'Prompt version manager'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Link to="/prompts" style={backLinkStyle}>← Prompt Library</Link>
        <span style={{ color: C.dim, fontSize: 12 }}>·</span>
        <span style={{ fontFamily: F.mono, fontSize: 12, color: C.cyan }}>{promptKey}</span>
        {def && (
          <>
            <span style={{ color: C.dim, fontSize: 12 }}>·</span>
            <StatusBadge label={def.category} color={C.dim} />
          </>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setNewVersionOpen(true)}
          style={primaryButtonStyle}
          disabled={loading || !versions}
          title={
            loading || !versions
              ? 'Loading versions…'
              : activeVersion
                ? 'Clone from the active version and create a new draft'
                : 'Create a new version from scratch (no active version found to clone from)'
          }
        >
          + New Version
        </button>
      </div>

      {errorMsg && (
        <GlassPanel style={{ borderColor: `${C.coral}60`, marginBottom: 16 }}>
          <span style={{ color: C.coral, fontFamily: F.mono, fontSize: 12 }}>
            {errorMsg}
          </span>
        </GlassPanel>
      )}

      {loading && !versions ? (
        <LoadingShimmer height={140} />
      ) : versions && versions.length > 0 ? (
        <VersionsTable
          versions={versions}
          onView={v => setViewVersionId(v.id)}
          onTest={v => setTestVersionId(v.id)}
          onActivate={v => setActivateCandidate(v)}
        />
      ) : (
        <EmptyState title="No versions yet. Use the seeder or create one." />
      )}

      {newVersionOpen && (
        <NewVersionDrawer
          promptKey={promptKey}
          activeVersionId={activeVersion?.id ?? null}
          onClose={() => setNewVersionOpen(false)}
          onCreated={() => { setNewVersionOpen(false); refresh(); }}
        />
      )}

      {viewVersionId && (
        <ViewVersionDrawer
          versionId={viewVersionId}
          onClose={() => setViewVersionId(null)}
        />
      )}

      {testVersionId && (
        <TestVersionModal
          versionId={testVersionId}
          onClose={() => setTestVersionId(null)}
        />
      )}

      {activateCandidate && (
        <ActivateConfirm
          candidate={activateCandidate}
          currentActive={activeVersion}
          onClose={() => setActivateCandidate(null)}
          onActivated={() => { setActivateCandidate(null); refresh(); }}
        />
      )}
    </EnginePageShell>
  );
}

// ─── Versions Table ─────────────────────────────────────────────────────────

function VersionsTable({
  versions,
  onView, onTest, onActivate,
}: {
  versions: PromptVersionSummary[];
  onView:     (v: PromptVersionSummary) => void;
  onTest:     (v: PromptVersionSummary) => void;
  onActivate: (v: PromptVersionSummary) => void;
}) {
  const sorted = [...versions].sort((a, b) => b.version - a.version);

  return (
    <GlassPanel>
      <SectionLabel>Version History</SectionLabel>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Version</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Model</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Temp</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Max Tokens</th>
            <th style={thStyle}>Author Notes</th>
            <th style={thStyle}>Created</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(v => (
            <tr key={v.id}>
              <td style={tdStyle}>
                <span style={{ fontFamily: F.mono, color: C.cyan, fontWeight: 600 }}>
                  v{v.version}
                </span>
              </td>
              <td style={tdStyle}>
                <StatusBadge label={v.status} color={statusColor(v.status)} />
              </td>
              <td style={{ ...tdStyle, fontFamily: F.mono, fontSize: 11 }}>{v.modelName}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: F.mono, fontSize: 11 }}>
                {v.temperature.toFixed(2)}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: F.mono, fontSize: 11 }}>
                {v.maxTokens.toLocaleString()}
              </td>
              <td style={{ ...tdStyle, fontSize: 11, color: C.muted, maxWidth: 280 }}>
                {v.authorNotes ?? <span style={{ color: C.dim }}>—</span>}
              </td>
              <td style={{ ...tdStyle, fontSize: 11, color: C.dim, fontFamily: F.mono }}>
                {new Date(v.createdAt).toLocaleString()}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button onClick={() => onView(v)}     style={linkButtonStyle}>view</button>
                <button onClick={() => onTest(v)}     style={linkButtonStyle}>test</button>
                {v.status !== 'active' && (
                  <button onClick={() => onActivate(v)} style={{ ...linkButtonStyle, color: C.neon }}>
                    activate
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GlassPanel>
  );
}

// ─── New Version Drawer ─────────────────────────────────────────────────────

function NewVersionDrawer({
  promptKey, activeVersionId, onClose, onCreated,
}: {
  promptKey: string;
  activeVersionId: string | null;
  onClose:    () => void;
  onCreated:  () => void;
}) {
  const { createApi } = useApiClient();
  const [systemText, setSystemText]     = useState('');
  const [userText, setUserText]         = useState('');
  const [modelName, setModelName]       = useState('gpt-4.1');
  const [temperature, setTemperature]   = useState('0.3');
  const [maxTokens, setMaxTokens]       = useState('4000');
  const [authorNotes, setAuthorNotes]   = useState('');
  const [loading, setLoading]           = useState(activeVersionId !== null);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // Pre-fill from the currently-active version if there is one. If not,
  // open with sensible defaults — the user can still author a version
  // from scratch.
  useEffect(() => {
    if (activeVersionId === null) return;
    (async () => {
      try {
        const api = await createApi();
        const res = await api.get<PromptVersionFull>(`/api/v1/prompts/versions/${activeVersionId}`);
        setSystemText(res.data.systemPromptText);
        setUserText(res.data.userTemplateText);
        setModelName(res.data.modelName);
        setTemperature(res.data.temperature.toString());
        setMaxTokens(res.data.maxTokens.toString());
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load active version');
      } finally {
        setLoading(false);
      }
    })();
  }, [activeVersionId, createApi]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const api = await createApi();
      await api.post(`/api/v1/prompts/definitions/${encodeURIComponent(promptKey)}/versions`, {
        systemPromptText: systemText,
        userTemplateText: userText,
        modelName,
        temperature: parseFloat(temperature),
        maxTokens:   parseInt(maxTokens, 10),
        authorNotes: authorNotes || null,
      });
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const loadFile = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.md';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const text = await file.text();
      setSystemText(text);
    };
    input.click();
  };

  return (
    <Drawer title="New Prompt Version" onClose={onClose} widthPx={720}>
      {loading ? (
        <LoadingShimmer height={120} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="System Prompt Text" hint="The instruction block. Paste or load from file.">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>
                {systemText.length.toLocaleString()} chars · {systemText.split('\n').length} lines
              </span>
              <button onClick={loadFile} style={linkButtonStyle}>load from .txt file</button>
            </div>
            <textarea
              value={systemText}
              onChange={e => setSystemText(e.target.value)}
              spellCheck={false}
              style={{ ...textareaStyle, minHeight: 240 }}
            />
          </Field>

          <Field label="User Template Text" hint="The {{placeholder}}-interpolated per-call template. Usually unchanged.">
            <textarea
              value={userText}
              onChange={e => setUserText(e.target.value)}
              spellCheck={false}
              style={{ ...textareaStyle, minHeight: 120 }}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
            <Field label="Model Name">
              <input
                value={modelName}
                onChange={e => setModelName(e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Temperature">
              <input
                value={temperature}
                onChange={e => setTemperature(e.target.value)}
                style={inputStyle}
                inputMode="decimal"
              />
            </Field>
            <Field label="Max Tokens">
              <input
                value={maxTokens}
                onChange={e => setMaxTokens(e.target.value)}
                style={inputStyle}
                inputMode="numeric"
              />
            </Field>
          </div>

          <Field label="Author Notes" hint="Short changelog for audit — shown in the version history.">
            <input
              value={authorNotes}
              onChange={e => setAuthorNotes(e.target.value)}
              placeholder="e.g. v3: engineering-work patterns + accuracy mandate"
              style={inputStyle}
            />
          </Field>

          {error && <ErrorLine message={error} />}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button onClick={onClose} style={secondaryButtonStyle} disabled={submitting}>
              Cancel
            </button>
            <button onClick={submit} style={primaryButtonStyle} disabled={submitting || !systemText || !modelName}>
              {submitting ? 'Creating…' : 'Create Draft Version'}
            </button>
          </div>

          <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginTop: 4 }}>
            New versions start as <span style={{ color: C.amber }}>draft</span>. Activate from the version list to go live.
          </div>
        </div>
      )}
    </Drawer>
  );
}

// ─── View Version Drawer (decrypted) ────────────────────────────────────────

function ViewVersionDrawer({
  versionId, onClose,
}: { versionId: string; onClose: () => void }) {
  const { createApi } = useApiClient();
  const [v, setV]         = useState<PromptVersionFull | null>(null);
  const [err, setErr]     = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const api = await createApi();
        const res = await api.get<PromptVersionFull>(`/api/v1/prompts/versions/${versionId}`);
        setV(res.data);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : 'Failed to load version');
      }
    })();
  }, [createApi, versionId]);

  return (
    <Drawer title={v ? `Version v${v.version} · ${v.status}` : 'Version'} onClose={onClose} widthPx={720}>
      {err && <ErrorLine message={err} />}
      {!v && !err && <LoadingShimmer height={180} />}
      {v && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <InfoRow label="Model" value={v.modelName} />
          <InfoRow label="Temperature" value={v.temperature.toFixed(2)} />
          <InfoRow label="Max Tokens" value={v.maxTokens.toLocaleString()} />
          <InfoRow label="Created" value={new Date(v.createdAt).toLocaleString()} />
          {v.authorNotes && <InfoRow label="Notes" value={v.authorNotes} />}

          <Field label="System Prompt Text">
            <CopyableBlock text={v.systemPromptText} />
          </Field>
          <Field label="User Template Text">
            <CopyableBlock text={v.userTemplateText} />
          </Field>
        </div>
      )}
    </Drawer>
  );
}

// ─── Test Modal ─────────────────────────────────────────────────────────────

function TestVersionModal({
  versionId, onClose,
}: { versionId: string; onClose: () => void }) {
  const { createApi } = useApiClient();
  const [conversationId, setConversationId] = useState('');
  const [running, setRunning]     = useState(false);
  const [result, setResult]       = useState<TestResult | null>(null);
  const [err, setErr]             = useState<string | null>(null);

  const run = async () => {
    const id = parseInt(conversationId, 10);
    if (!Number.isFinite(id) || id <= 0) {
      setErr('Enter a valid ImportedConversation Id');
      return;
    }
    setRunning(true);
    setErr(null);
    setResult(null);
    try {
      const api = await createApi();

      // The /test endpoint takes raw prompt text, not a version id — it's
      // a literal dry-run. Fetch the version's decrypted text first, then
      // submit the expanded payload. Two sequential calls, not parallel,
      // because both hit the scoped DbContext.
      const version = await api.get<PromptVersionFull>(`/api/v1/prompts/versions/${versionId}`);

      const res = await api.post<TestResult>('/api/v1/prompts/test', {
        systemPromptText: version.data.systemPromptText,
        userTemplateText: version.data.userTemplateText,
        modelName:        version.data.modelName,
        temperature:      version.data.temperature,
        maxTokens:        version.data.maxTokens,
        conversationId:   id,
      });
      setResult(res.data);
      if (!res.data.success && res.data.error) {
        setErr(res.data.error);
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setRunning(false);
    }
  };

  let prettyOutput = result?.outputJson ?? '';
  try {
    if (result?.outputJson) prettyOutput = JSON.stringify(JSON.parse(result.outputJson), null, 2);
  } catch { /* not JSON, show raw */ }

  return (
    <Drawer title="Dry-Run Test" onClose={onClose} widthPx={720}>
      <Field label="ImportedConversation Id" hint="The conversation to classify against this version. No DB writes.">
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={conversationId}
            onChange={e => setConversationId(e.target.value)}
            placeholder="e.g. 283"
            inputMode="numeric"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={run} style={primaryButtonStyle} disabled={running}>
            {running ? 'Running…' : 'Run Test'}
          </button>
        </div>
      </Field>

      {err && <ErrorLine message={err} />}

      {result && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <StatPill label="Outcome"    value={result.success ? 'ok' : 'failed'} color={result.success ? C.neon : C.coral} />
            <StatPill label="Latency"    value={`${result.latencyMs} ms`} color={C.cyan} />
            <StatPill label="Input Tok"  value={result.tokensIn.toLocaleString()} color={C.blue} />
            <StatPill label="Output Tok" value={result.tokensOut.toLocaleString()} color={C.violet} />
            <StatPill label="Cost"       value={`$${result.costUSD.toFixed(4)}`} color={C.amber} />
          </div>
          {result.outputJson && (
            <Field label="Output JSON">
              <CopyableBlock text={prettyOutput} />
            </Field>
          )}
        </div>
      )}
    </Drawer>
  );
}

// ─── Activate Confirmation ──────────────────────────────────────────────────

function ActivateConfirm({
  candidate, currentActive, onClose, onActivated,
}: {
  candidate:      PromptVersionSummary;
  currentActive?: PromptVersionSummary | null;
  onClose:     () => void;
  onActivated: () => void;
}) {
  const { createApi } = useApiClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      const api = await createApi();
      await api.put(`/api/v1/prompts/versions/${candidate.id}/activate`, { deactivate: false });
      onActivated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Activate failed');
      setBusy(false);
    }
  };

  return (
    <Drawer title={`Activate v${candidate.version}`} onClose={onClose} widthPx={520}>
      <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.55, margin: 0 }}>
        Activating <strong style={{ color: C.neon }}>v{candidate.version}</strong> will immediately
        make it the live prompt for all tenants using the platform default.
        {currentActive && (
          <>
            {' '}The currently active version{' '}
            <strong style={{ color: C.amber }}>v{currentActive.version}</strong> will be deactivated.
          </>
        )}
      </p>

      <div style={{ marginTop: 14, padding: 10, background: C.surface, borderRadius: 8 }}>
        <InfoRow label="Version"  value={`v${candidate.version}`} />
        <InfoRow label="Model"    value={candidate.modelName} />
        <InfoRow label="Temp"     value={candidate.temperature.toFixed(2)} />
        <InfoRow label="MaxTok"   value={candidate.maxTokens.toLocaleString()} />
        {candidate.authorNotes && <InfoRow label="Notes" value={candidate.authorNotes} />}
      </div>

      {err && <ErrorLine message={err} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={secondaryButtonStyle} disabled={busy}>
          Cancel
        </button>
        <button onClick={go} style={{ ...primaryButtonStyle, background: `${C.neon}25`, borderColor: C.neon, color: C.neon }} disabled={busy}>
          {busy ? 'Activating…' : 'Activate Version'}
        </button>
      </div>
    </Drawer>
  );
}

// ─── Shared UI primitives (local to this page) ──────────────────────────────

function Drawer({
  title, onClose, children, widthPx = 640,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  widthPx?: number;
}) {
  // Portal to document.body to escape EnginePageShell's stacking context.
  // position: fixed re-parents to the nearest ancestor that creates a new
  // stacking context (anything with transform / filter / will-change), so
  // without the portal the drawer anchors to a glass panel and pushes the
  // content down the page instead of overlaying it.
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 10000,
        display: 'flex', justifyContent: 'flex-end',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: widthPx, maxWidth: '100%', height: '100%',
          background: C.panel, borderLeft: `1px solid ${C.faint}`,
          padding: 20, overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-24px 0 60px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontFamily: F.display, fontSize: 18, color: C.text }}>{title}</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={closeButtonStyle}>✕</button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, marginBottom: 4, fontFamily: F.mono }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.dim, fontFamily: F.mono, minWidth: 90 }}>
        {label}
      </span>
      <span style={{ fontSize: 12, color: C.text, fontFamily: F.mono }}>{value}</span>
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ padding: '6px 10px', border: `1px solid ${color}40`, borderRadius: 6, background: `${color}10` }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.dim, fontFamily: F.mono }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color, fontFamily: F.mono, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function CopyableBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={copy} style={{ ...linkButtonStyle, position: 'absolute', top: 6, right: 6 }}>
        {copied ? 'copied' : 'copy'}
      </button>
      <pre style={{
        margin: 0,
        background: C.bg,
        padding: 12,
        borderRadius: 6,
        border: `1px solid ${C.faint}`,
        color: C.text,
        fontFamily: F.mono,
        fontSize: 11,
        lineHeight: 1.5,
        maxHeight: 340,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
      }}>{text}</pre>
    </div>
  );
}

function ErrorLine({ message }: { message: string }) {
  return (
    <div style={{ fontSize: 11, color: C.coral, fontFamily: F.mono, padding: '6px 10px', background: `${C.coral}15`, borderRadius: 6 }}>
      {message}
    </div>
  );
}

// ─── Styles & helpers ───────────────────────────────────────────────────────

function statusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'active':   return C.neon;
    case 'draft':    return C.amber;
    case 'archived': return C.dim;
    default:         return C.dim;
  }
}

const backLinkStyle: React.CSSProperties = {
  fontFamily: F.mono, fontSize: 12, color: C.dim, textDecoration: 'none',
};

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontFamily: F.mono };
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', fontSize: 10, textTransform: 'uppercase',
  letterSpacing: '0.05em', color: C.dim, borderBottom: `1px solid ${C.dim}30`,
};
const tdStyle: React.CSSProperties = {
  padding: '10px 12px', borderBottom: `1px solid ${C.dim}15`, verticalAlign: 'top', fontSize: 12,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '6px 14px', background: `${C.cyan}25`, border: `1px solid ${C.cyan}`, color: C.cyan,
  borderRadius: 6, fontFamily: F.mono, fontSize: 12, cursor: 'pointer',
};
const secondaryButtonStyle: React.CSSProperties = {
  padding: '6px 14px', background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted,
  borderRadius: 6, fontFamily: F.mono, fontSize: 12, cursor: 'pointer',
};
const linkButtonStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', color: C.cyan, fontFamily: F.mono, fontSize: 11,
  cursor: 'pointer', padding: '2px 6px', marginLeft: 4,
};
const closeButtonStyle: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted, width: 28, height: 28,
  borderRadius: 6, cursor: 'pointer', fontFamily: F.mono,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', background: C.bg, border: `1px solid ${C.faint}`,
  color: C.text, fontFamily: F.mono, fontSize: 12, borderRadius: 6, outline: 'none',
};
const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  fontSize: 11,
  lineHeight: 1.55,
};
